import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Key, Activity, Database, Users, AlertTriangle, HelpCircle, Globe, Shield, Mail, FileText, Archive } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ChangePassword } from "../../components/shared/ChangePassword";
import ManageTenants from "./ManageTenants";
import GDPRCompliance from "./GDPRCompliance";
import SystemEmailConfig from "./SystemEmailConfig";
import PlatformActivity from "./PlatformActivity";
import DataManager from "./DataManager";
import DbBackups from "./DbBackups";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function AdminDashboard() {
  const [tab, setTab] = useState("metrics");
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== "admin") navigate("/admin");
  }, [user]);

  // iter139: System Admin logs back into `/admin` (no tenant slug), not `/`.
  const handleLogout = () => { logout(); navigate("/admin"); };

  const tabs = [
    { id: "metrics", icon: Activity, label: "System" },
    { id: "tenants", icon: Globe, label: "Tenants" },
    { id: "activity", icon: FileText, label: "Platform Activity" },
    { id: "email", icon: Mail, label: "Email" },
    { id: "gdpr", icon: Shield, label: "GDPR" },
    { id: "data", icon: Database, label: "Data Manager" },
    { id: "backups", icon: Archive, label: "Backups" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/mymaitree-logo.png" alt="My Maitree" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <p className="text-slate-400 text-xs">System Administrator</p>
              <h1 className="text-sm font-bold text-[#1e3a5f]">{user?.name || "Admin"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("changePassword")} className={`p-2 rounded-full hover:bg-slate-100 ${tab === "changePassword" ? "text-[#1e3a5f]" : "text-slate-400"}`}><Key className="w-5 h-5" /></button>
            <button onClick={() => navigate("/help?role=admin")} className="p-2 rounded-full hover:bg-slate-100 text-slate-400" data-testid="help-btn"><HelpCircle className="w-5 h-5" /></button>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-red-50 text-red-500" data-testid="logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        {tab !== "changePassword" && (
          <div className="max-w-5xl mx-auto flex border-t overflow-x-auto">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 shrink-0 whitespace-nowrap px-3 py-3 flex items-center justify-center gap-1.5 text-sm font-medium border-b-2 ${tab === t.id ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-slate-400 hover:text-slate-600"}`} data-testid={`nav-${t.id}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="max-w-5xl mx-auto p-6">
        {tab === "metrics" && <SystemMetrics />}
        {tab === "tenants" && <ManageTenants />}
        {tab === "activity" && <PlatformActivity />}
        {tab === "email" && <SystemEmailConfig />}
        {tab === "gdpr" && <GDPRCompliance />}
        {tab === "data" && <DataManager />}
        {tab === "backups" && <DbBackups />}
        {tab === "changePassword" && <ChangePassword onBack={() => setTab("metrics")} role="admin" />}
      </div>
    </div>
  );
}

function SystemMetrics() {
  const [data, setData] = useState(null);
  const { authAxios } = useAuth();
  useEffect(() => { authAxios.get("/system/metrics").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-center py-8 text-slate-400">Loading system metrics...</div>;

  const u = data.users;
  const userCards = [
    { label: "Finance Admins", total: u.finance_admins.total, active: u.finance_admins.active, color: "text-purple-600", bg: "bg-purple-50" },
    { label: "Accountants", total: u.accountants.total, active: u.accountants.active, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Merchants", total: u.merchants.total, active: u.merchants.active, color: "text-green-600", bg: "bg-green-50" },
    { label: "Members", total: u.members.total, active: u.members.active, color: "text-[#1e3a5f]", bg: "bg-blue-50" },
    { label: "Guests", total: u.guests.total, active: u.guests.active, color: "text-teal-600", bg: "bg-teal-50" },
  ];

  const dv = data.data_volumes;

  return (
    <div className="space-y-4" data-testid="system-metrics">
      {/* Users */}
      <h2 className="text-lg font-bold text-[#1e3a5f]">Users</h2>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {userCards.map(c => (
          <div key={c.label} className={`${c.bg} rounded-2xl p-4 shadow-sm`}>
            <p className={`text-2xl font-extrabold ${c.color}`}>{c.active}</p>
            <p className="text-slate-500 text-xs">{c.label}</p>
            {c.total !== c.active && <p className="text-slate-400 text-[10px]">{c.total} total ({c.total - c.active} inactive)</p>}
          </div>
        ))}
      </div>

      {/* Data Volumes */}
      <h2 className="text-lg font-bold text-[#1e3a5f] mt-2">Data Volumes</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Wallet Txns", value: dv.wallet_transactions, icon: "💳" },
          { label: "Subscriptions", value: dv.subscription_transactions, icon: "📋" },
          { label: "Events", value: dv.events, icon: "📅" },
          { label: "Expenses", value: dv.expenses, icon: "📄" },
          { label: "Settlements", value: dv.settlements, icon: "🤝" },
          { label: "SEPA Mandates", value: dv.sepa_mandates, icon: "🏦" },
          { label: "Attachments", value: dv.expense_attachments, icon: "📎" },
          { label: "Collections", value: data.health.total_collections, icon: "🗄️" },
        ].map(d => (
          <div key={d.label} className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <p className="text-xl font-extrabold text-[#1e3a5f]">{d.value}</p>
            <p className="text-slate-500 text-[10px]">{d.label}</p>
          </div>
        ))}
      </div>

      {/* Activity Trend */}
      {data.activity_trend.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-xs font-bold text-slate-500 mb-2">Wallet Transaction Activity (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={data.activity_trend}>
              <XAxis dataKey="date" tick={{ fontSize: 8 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#1e3a5f" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Health */}
      <h2 className="text-lg font-bold text-[#1e3a5f] mt-2">Health</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={`rounded-2xl p-4 shadow-sm ${data.health.stale_members > 0 ? "bg-amber-50" : "bg-green-50"}`}>
          <div className="flex items-center gap-2 mb-1">
            {data.health.stale_members > 0 ? <AlertTriangle className="w-5 h-5 text-amber-500" /> : <Activity className="w-5 h-5 text-green-500" />}
            <p className="font-bold text-sm">Stale Members</p>
          </div>
          <p className={`text-2xl font-extrabold ${data.health.stale_members > 0 ? "text-amber-600" : "text-green-600"}`}>{data.health.stale_members}</p>
          <p className="text-slate-500 text-[10px]">Active members with no transactions in 90 days</p>
        </div>
        <div className={`rounded-2xl p-4 shadow-sm ${data.health.stale_guests > 0 ? "bg-amber-50" : "bg-green-50"}`}>
          <div className="flex items-center gap-2 mb-1">
            {data.health.stale_guests > 0 ? <AlertTriangle className="w-5 h-5 text-amber-500" /> : <Activity className="w-5 h-5 text-green-500" />}
            <p className="font-bold text-sm">Stale Guests</p>
          </div>
          <p className={`text-2xl font-extrabold ${data.health.stale_guests > 0 ? "text-amber-600" : "text-green-600"}`}>{data.health.stale_guests}</p>
          <p className="text-slate-500 text-[10px]">Active guests with no transactions in 90 days</p>
        </div>
      </div>
    </div>
  );
}
